<template>
  <panel-layout
    id="course-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="course" class="flex items-center space-x-2">
        <o-history-link :item="course" model="Course" icon="history" slim flat />
      </div>
    </template>

    <template #heading>
      <div v-if="course" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100"
          >
            {{ course.isNewRecord ? $t('pages.courseType.edit.newCourse') : $t('pages.courseType.edit.courseNamed', { name: course.name }) }}
          </h2>
          <div v-if="!course.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200">
              <span class="text-gray-700 dark:text-gray-200">{{ course.infoLabel }}</span>
            </p>
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="course" ref="form" class="h-full w-full p-4">
      <o-field-group
        :label="$t('general.title')"
        :name="$t('general.name')"
        rules="required"
        :validation-value="course.name"
        class="mb-4 block"
      >
        <text-field
          v-model="course.name"
          :label="$t('general.name')"
          :placeholder="$t('pages.course.enterCourseNamePlaceholder')"
          required
        />
      </o-field-group>

      <div v-if="!course.isNewRecord" class="flex space-x-4 mb-6">
        <o-field-group :label="$t('components.course.actions.image.imageLabel')" :info="$t('pages.courseType.edit.imageVisibleInfo')">
          <avatar-field :model="course" @click="handleUpdateImage" />
        </o-field-group>
      </div>

      <o-field-group
        :label="$t('general.information')"
        :name="$t('general.information')"
        rules="required"
        :validation-value="course.information"
        class="mb-12 block"
      >
        <text-area-field
          v-model="course.information"
          :label="$t('general.information')"
          :placeholder="$t('pages.course.enterCourseInfoPlaceholder')"
          required
        />
      </o-field-group>

      <template v-if="!course.isNewRecord && courseRuns">
        <div class="flex items-center justify-between mb-2">
          <o-field-group :label="$t('pages.courseType.edit.runs')" :info="$t('pages.courseType.edit.runsInfo')" />

          <o-panel-link variant="primary" tag="o-button" :path="`/course/create?id=${course.idCourse}`">
            {{ $t('pages.courseType.edit.createRun') }}
          </o-panel-link>
        </div>


        <p class="font-medium mb-1">{{ $t('pages.courseType.edit.active') }}</p>
        <div class="border rounded dark:border-gray-800 bg-gray-50 dark:bg-gray-900 mb-8">
          <div v-if="!activeCourseRuns.length" class="p-4">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('pages.courseType.edit.noActiveRuns') }}
            </p>
          </div>

          <div v-if="activeCourseRuns.length" class="divide-x">
            <div v-for="courseRun in activeCourseRuns" :key="courseRun.idCourseRun" class="p-2 border-b dark:border-gray-800 last:border-none flex items-center justify-between">
              <div>
                <p class="font-medium">{{ courseRun?.information }} <span class="font-normal opacity-70">(#{{ courseRun.idCourseRun }})</span></p>
                <p v-if="courseRun?.dates.length > 0" class="text-sm opacity-70">{{ $t('pages.courseType.edit.starting', { date: $date.convert(courseRun?.dates[0], undefined, $date.presets.long) }) }}</p>
                <p v-else class="text-sm opacity-70">{{ $t('general.noStartDate') }}</p>
              </div>

              <div class="flex items-center space-x-1">
                <o-panel-link
                  tag="o-button"
                  size="sm"
                  flat
                  outline
                  icon="edit"
                  :path="`/course/edit?idCourseRun=${courseRun.idCourseRun}&idCourse=${course.idCourse}`"
                />

                <o-button flat size="sm" icon="delete" variant="danger" @click="handleDeleteCourseRun(courseRun)" />
              </div>
            </div>
          </div>
        </div>

        <p class="font-medium mb-1">{{ $t('pages.courseType.edit.expiredPast') }}</p>
        <div class="border rounded dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
          <div v-if="!expiredCourseRuns.length" class="p-4">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('pages.courseType.edit.noExpiredRuns') }}
            </p>
          </div>

          <div v-if="expiredCourseRuns.length" class="divide-x">
            <div v-for="courseRun in expiredCourseRuns" :key="courseRun.idCourseRun" class="p-2 border-b dark:border-gray-800 last:border-none flex items-center justify-between">
              <div>
                <p class="font-medium">{{ courseRun?.information }} <span class="font-normal opacity-70">(#{{ courseRun.idCourseRun }})</span></p>
                <p v-if="courseRun?.dates" class="text-sm opacity-70">{{ $t('pages.courseType.edit.started', { date: $date.convert(courseRun?.dates[0], undefined, $date.presets.long) }) }}</p>
                <p v-else class="text-sm opacity-70">{{ $t('general.noStartDate') }}</p>
              </div>

              <div class="flex items-center space-x-1">
                <o-panel-link
                  tag="o-button"
                  size="sm"
                  flat
                  outline
                  icon="edit"
                  :path="`/course/edit?idCourseRun=${courseRun.idCourseRun}&idCourse=${course.idCourse}`"
                />

                <o-button flat size="sm" icon="delete" variant="danger" @click="handleDeleteCourseRun(courseRun)" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </o-form>

    <template #footer>
      <div v-if="course">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              variant="primary"
              outline
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
            <o-button
              variant="primary"
              icon="save"
              class="mr-2"
              @click="save(true)"
            >
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="course">
      <deleteDialog ref="deleteDialog" @success="cancel(true)" />

      <deleteCourseRunDialog ref="deleteCourseRunDialog" @success="fetchCourseRuns" />
    </template>

    <template v-if="course">
      <update-image-dialog
        ref="updateImageDialog"
        :course="course"
        @update:course="handleUpdateImageSuccess"
      />
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      icon="course"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">

// @ts-ignore
import DeleteDialog from '@/components/course/actions/Delete.vue'
// @ts-ignore
import UpdateImageDialog from '@/components/course/actions/Image.vue'
// @ts-ignore
import DeleteCourseRunDialog from '@/components/courseRun/actions/Delete.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab, OAlert, OAvatar } from '@/components/elements'
import { TextField, TextAreaField, AvatarField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $panel: $panelPlugin, $date, $config, $auth, $i18n } = useNuxtApp()
const router = useRouter()

const course = ref<any>(null)
const courseRuns = ref<any>(null)
const errors = ref(false)
const loading = ref(true)

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()
const deleteCourseRunDialog = ref<any>()
const updateImageDialog = ref<any>()
const form = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(course)

const activeCourseRuns = computed(() => {
  const today = $date.format(new Date())
  return courseRuns.value?.items.filter((run: any) => {
    return !run.dates[0] || run.dates[0] >= today
  })
})

const expiredCourseRuns = computed(() => {
  const today = $date.format(new Date())
  return courseRuns.value?.items.filter((run: any) => {
    return run.dates[0] < today
  })
})

const isDev = computed(() => $config.public.instance === 'dev')
const isAdmin = computed(() => $auth?.info?.adminUser)

const fetchCourse = async (id?: string) => {
  try {
    if (!id) {
      course.value = (await $api.course.defaults({} as any)).data
    } else {
      course.value = (await $api.course.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
}

const fetchCourseRuns = async () => {
  try {
    if (!course.value.isNewRecord) {
      courseRuns.value = await $api.course.listRuns(course.value)
      console.log(courseRuns.value)
    }
  } catch (e) {
    console.log(e)
  }
}

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true
  errors.value = false

  try {
    const isNewRecord = course.value.isNewRecord
    const result = (await $api.course.save(course.value)).data

    if (result) {
      course.value = result
      snapshot()

      $notification.toast({
        title: 'Course details saved',
        text: 'The course details were successfully saved',
        variant: 'success',
      })

      $bus.emit.updated('Course', result)

      if (close) {
        panel.close()
      } else if (isNewRecord) {
        // Preserve pre-refactor behaviour: after saving a new course type,
        // open the CourseRun edit view. (The legacy code navigated to
        // /course/edit?id=X — keeping the same path through the shim.)
        $panelPlugin.create(`/course/edit?id=${result.idCourse}`)
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleDelete = () => {
  deleteDialog.value.open(course.value)
}

const handleDeleteCourseRun = (courseRun: any) => {
  deleteCourseRunDialog.value.open(courseRun)
}

const handleUpdateImageSuccess = (updatedCourse: any) => {
  course.value = updatedCourse
  snapshot()
  $bus.emit.updated('Course', updatedCourse)
}

const handleUpdateImage = () => {
  updateImageDialog.value.open()
}

watch(() => props.id, async (id) => {
  loading.value = true
  await fetchCourse(id)
  fetchCourseRuns()
  loading.value = false
}, { immediate: true })
</script>
